import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Translate } from "@phosphor-icons/react"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"
import { uuidv4 } from "@/lib/generate-uuidv4"
import { convertISOCodeToLanguage } from "@/lib/language-to-iso-code"
import { renderMessageLines } from "@/modules/chats/components/chat-message/render-message-lines"
import { translateQuery } from "@/modules/chats/services/ai/translateQuery"
import { ChatPatient } from "@/modules/chats/types/chat-thread"

export const NonAutoTranslatedPatientMessageContent = (props: {
  patientOriginalContent: string
  appointmentId: string
  patient: ChatPatient
}) => {
  const { t } = useTranslation()
  const [translatedText, setTranslatedText] = useState("")
  const [loading, setLoading] = useState(false)
  const currentLanguage = useCurrentLanguage()
  const longLanguage = convertISOCodeToLanguage(currentLanguage.toUpperCase()) ?? "ENGLISH"

  const handleTranslation = async (textToTranslate: string) => {
    if (!textToTranslate) return

    setLoading(true)
    try {
      const aiFeatureId = uuidv4()
      const result = await translateQuery(props.patient.country, {
        messageRole: "PATIENT",
        aiRequestMetadata: {
          targetAgentLanguage: longLanguage,
          appointmentId: props.appointmentId,
          aiFeatureId,
          age: props.patient.age.years,
          gender: props.patient.gender,
          userInput: props.patientOriginalContent,
        },
      })
      if (result && result.translate.result === "SUCCESS") {
        setTranslatedText(result.translate.text)

        FaroInsightsWrapper.pushEvent("aiFeature_translatePatient", {
          aiFeatureId,
          feature: "Translate",
          fileHash: result.translate.fileHash,
          aiRequestDurationMs: result.translate.aiRequestDurationMs.toString(),
          aiAnonymizationRequestDurationMs: result.translate.aiAnonymizationRequestDurationMs.toString(),
          sentAt: new Date().toISOString(),
        })
      }
    } catch (error) {
      console.log("Text translation request is failed", error)
    } finally {
      setLoading(false)
    }
  }

  const getMessage = () =>
    translatedText ? (
      <div className="text-body-sm">{translatedText}</div>
    ) : (
      renderMessageLines(props.patientOriginalContent)
    )

  const renderButton = () =>
    loading ? (
      <span className="text-primary w-fit text-xs">{t("chat.translating")}</span>
    ) : (
      <button
        className="text-primary w-fit cursor-pointer text-xs"
        onClick={() => handleTranslation(props.patientOriginalContent)}>
        {t("chat.see.translation")}
      </button>
    )

  return (
    <div className="grid h-fit gap-1 text-sm" data-testid="chat-message-from-patient">
      {getMessage()}
      {translatedText ? (
        <button className="text-primary w-fit cursor-pointer text-xs" onClick={() => setTranslatedText("")}>
          <span className="flex items-center gap-1">
            <Translate size={16} className="text-primary" />
            {t("chat.see.original")}
          </span>
        </button>
      ) : (
        renderButton()
      )}
    </div>
  )
}
